import { useState, useCallback } from "react";
import { generate, sessions, BACKEND_URL } from "@/lib/api";
import {
  Wand2,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Shuffle,
  BookOpen,
  AlertTriangle,
  Pencil,
  Save,
} from "lucide-react";

const COUNCIL_URL = "https://yourvoicebucks.citizenspace.com/planning/2aa4b313/consultation/";

const EDITABLE_FIELDS = [
  "connection_to_site",
  "suitability_reason",
  "opportunities",
  "community_assets_detail",
  "further_context",
];

export default function GeneratorView({ sites }) {
  const [seed, setSeed] = useState(42);
  const [prefillData, setPrefillData] = useState(null);
  const [bookmarklet, setBookmarklet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const randomSeed = () => setSeed(Math.floor(Math.random() * 99999));

  const handleGeneratePreview = async () => {
    setLoading(true);
    setError(null);
    setBookmarklet(null);
    try {
      const pfRes = await generate.prefill(seed);
      setPrefillData(pfRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Preview generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSiteField = useCallback((siteIndex, fieldKey, newValue) => {
    setPrefillData((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, sites: [...prev.sites] };
      updated.sites[siteIndex] = { ...updated.sites[siteIndex], [fieldKey]: newValue };
      return updated;
    });
  }, []);

  const handleCreateBookmarklet = async () => {
    if (!prefillData) return;
    setSaving(true);
    setError(null);
    try {
      // Save edited data as a session
      const sessionRes = await sessions.create(prefillData.sites);
      const token = sessionRes.data.token;

      // Generate bookmarklet using session token
      const bmRes = await generate.bookmarklet(seed, BACKEND_URL, null, token);
      setBookmarklet(bmRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Bookmarklet creation failed");
    } finally {
      setSaving(false);
    }
  };

  const copyBookmarklet = async () => {
    if (!bookmarklet) return;
    try {
      await navigator.clipboard.writeText(bookmarklet.bookmarklet);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = bookmarklet.bookmarklet;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="tab-content" data-testid="generator-tab">
      <h2 className="tab-title">Bookmarklet Generator</h2>
      <p className="tab-desc">
        Generate varied responses for all {sites.length} sites, edit them, then create a bookmarklet
      </p>

      {/* Step 1: Generate Preview */}
      <div className="gen-controls">
        <div className="gen-seed-row">
          <label>Variation Seed</label>
          <div className="gen-seed-input">
            <input
              data-testid="seed-input"
              type="number"
              value={seed}
              onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
            />
            <button data-testid="random-seed-btn" className="btn-icon" onClick={randomSeed} title="Random seed">
              <Shuffle size={16} />
            </button>
          </div>
          <span className="gen-seed-hint">Different seeds produce different response combinations</span>
        </div>
        <button
          data-testid="generate-btn"
          className="btn-primary gen-btn"
          onClick={handleGeneratePreview}
          disabled={loading}
        >
          <Wand2 size={18} />
          <span>{loading ? "Generating..." : "1. Generate Preview"}</span>
        </button>
      </div>

      {error && (
        <div className="gen-error" data-testid="gen-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Step 2: Editable Preview + Create Bookmarklet */}
      {prefillData && (
        <>
          <div className="gen-edit-banner" data-testid="edit-banner">
            <div className="gen-edit-banner-left">
              <Pencil size={18} />
              <div>
                <strong>Review & Edit Responses</strong>
                <span>Text fields highlighted in green are editable — personalise before creating the bookmarklet</span>
              </div>
            </div>
            <button
              data-testid="create-bookmarklet-btn"
              className="btn-primary gen-btn"
              onClick={handleCreateBookmarklet}
              disabled={saving}
            >
              <Save size={18} />
              <span>{saving ? "Creating..." : "2. Create Bookmarklet"}</span>
            </button>
          </div>

          <div className="gen-preview" data-testid="prefill-preview">
            <h3>Response Preview (seed: {prefillData.seed})</h3>
            <p className="tab-desc">{prefillData.total} sites — click any text field to edit</p>
            <div className="gen-preview-list">
              {prefillData.sites.map((s, i) => (
                <PrefillCard
                  key={i}
                  site={s}
                  index={i}
                  total={prefillData.total}
                  onFieldChange={handleUpdateSiteField}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Bookmarklet Output */}
      {bookmarklet && (
        <div className="gen-output" data-testid="bookmarklet-output">
          <div className="gen-output-header">
            <h3>Your Bookmarklet</h3>
            <span className="gen-badge">{bookmarklet.total_sites} sites</span>
          </div>

          <div className="gen-bookmarklet-box">
            <div className="gen-bookmarklet-drag">
              <a
                href={bookmarklet.bookmarklet}
                onClick={(e) => e.preventDefault()}
                className="gen-bookmarklet-link"
                data-testid="bookmarklet-drag-link"
              >
                <Wand2 size={14} />
                AHT Fill
              </a>
              <span className="gen-drag-hint">Drag this to your bookmarks bar</span>
            </div>
            <button data-testid="copy-bookmarklet-btn" className="btn-secondary" onClick={copyBookmarklet}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? "Copied!" : "Copy Code"}</span>
            </button>
          </div>

          <div className="gen-instructions">
            <h4><BookOpen size={16} /> How to use</h4>
            <ol>
              <li>Drag the <strong>"AHT Fill"</strong> link above to your browser's bookmarks bar</li>
              <li>
                Open the council form:{" "}
                <a href={COUNCIL_URL} target="_blank" rel="noopener noreferrer" data-testid="council-form-link">
                  Council Form <ExternalLink size={12} />
                </a>
              </li>
              <li>Select <em>"As an individual resident"</em> and click Continue</li>
              <li>On the "Site response" page, click the <strong>AHT Fill</strong> bookmark</li>
              <li>Review the pre-filled data, then click <strong>Submit Response</strong></li>
              <li>For the next site page, click the bookmarklet again — it auto-advances</li>
              <li>Repeat until all {bookmarklet.total_sites} sites are done</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function PrefillCard({ site, index, total, onFieldChange }) {
  const [open, setOpen] = useState(index === 0);
  const isLast = index === total - 1;

  const FIELD_CONFIG = [
    { key: "site_reference", label: "Site Reference", type: "mono" },
    { key: "site_address", label: "Site Address" },
    { key: "connection_to_site", label: "Connection to Site", editable: true },
    { key: "is_suitable", label: "Is Suitable?", type: "badge", format: (v) => v ? "Yes" : "No" },
    { key: "suitability_reason", label: "Suitability Reason", editable: true },
    { key: "opportunities", label: "Opportunities", editable: true },
    { key: "has_infrastructure_issues", label: "Infrastructure Issues?", type: "badge", format: (v) => v ? "Yes" : "No" },
    { key: "environmental_features", label: "Environmental Features", editable: true, conditional: () => !!site.environmental_features },
    { key: "other_barriers", label: "Other Barriers/Challenges", editable: true, conditional: () => !!site.other_barriers },
    { key: "has_community_assets", label: "Community Assets?", type: "badge", format: (v) => v ? "Yes" : "No" },
    { key: "community_assets_detail", label: "Community Assets Detail", editable: true, conditional: () => site.has_community_assets },
    { key: "has_previous_promotion", label: "Previously Promoted?", type: "badge", format: (v) => v ? "Yes" : "No" },
    { key: "has_local_issues", label: "Local Issues?", type: "badge", format: (v) => v ? "Yes" : "No" },
    { key: "further_context", label: "Further Context", editable: true, conditional: () => true },
  ];

  return (
    <div className={`prefill-card ${open ? "open" : ""}`} data-testid={`prefill-card-${index}`}>
      <button className="prefill-card-header" onClick={() => setOpen(!open)}>
        <div className="prefill-card-title">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="prefill-idx">{index + 1}/{total}</span>
          <span className="prefill-ref">Ref {site.site_reference}</span>
          <span className="prefill-addr">{site.site_address}</span>
        </div>
        <span className={`prefill-parish ${site.site_parish}`}>
          {site.site_parish === "within" ? "Within" : "Outside"}
        </span>
      </button>
      {open && (
        <div className="prefill-fields">
          {FIELD_CONFIG.map((f) => {
            if (f.conditional && !f.conditional()) return null;
            const value = site[f.key];
            if (value === undefined || value === null) return null;

            return (
              <div key={f.key} className={`prefill-field ${f.editable ? "editable" : ""}`}>
                <span className="prefill-field-label">
                  {f.label}
                  {f.editable && <Pencil size={10} className="edit-icon" />}
                </span>
                {f.type === "mono" ? (
                  <code className="prefill-field-value mono">{value}</code>
                ) : f.type === "badge" ? (
                  <span className={`prefill-badge ${(f.format ? f.format(value) : value).toString().startsWith("Yes") ? "yes" : "no"}`}>
                    {f.format ? f.format(value) : value}
                  </span>
                ) : f.editable ? (
                  <textarea
                    className="prefill-textarea"
                    data-testid={`edit-${f.key}-${index}`}
                    value={value}
                    onChange={(e) => onFieldChange(index, f.key, e.target.value)}
                    rows={Math.max(2, Math.ceil((value || "").length / 90))}
                  />
                ) : (
                  <p className="prefill-field-value">{value}</p>
                )}
              </div>
            );
          })}
          <div className="prefill-field">
            <span className="prefill-field-label">Respond on further site?</span>
            <span className={`prefill-badge ${isLast ? "no" : "yes"}`}>
              {isLast ? "No (last site)" : "Yes (next site)"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
